/**
 * WHO WANTS AN AGENT PROFILE.
 *
 * The founder, 16 September 2026:
 *
 *   "there are two ways to add a property: 'I own it' and 'I manage it.' Let
 *    there be a prompt when one chooses the Manage option: landlord profiles
 *    can only add units they own. Please request for Orchid to add an Agent
 *    profile in order to add units that you manage. --> and then, there must be
 *    a button to 'Send Request' and this request must reach a desk in the
 *    Portal. Then we can gather stats about how many people want to add units
 *    they don't own but manage, and we'll have an idea about how to build the
 *    Agency profile."
 *
 * THE STATISTIC IS ONE ROW PER PERSON, NOT ONE PER PRESS, and that is enforced
 * in the platform by a UNIQUE on `requested_by` rather than trusted to a screen.
 * A person who presses the button three times is one person who wants this.
 * `times_asked` keeps the other number available without letting it be mistaken
 * for the first.
 *
 * AND THE UNIT COUNT IS OPTIONAL, WHICH IS WHY `answered` IS REPORTED BESIDE
 * THE TOTAL. Forty people managing two units each and four managing four
 * hundred are the same headcount and a completely different product -- but a
 * total built from a third of the replies, printed beside a headline count of
 * everybody, is a statistic that misleads the person who commissioned it.
 *
 * NO NAMES. `ofx_connect_code` identifies the account. Prohibition 6.
 *
 * WHAT IS NOT BUILT, SAID PLAINLY: there is no reply path. A desk can see who
 * asked and mark that it has been read; it cannot answer them from here.
 */

import { supabase, isSupabaseConfigured } from './supabaseClient';

const fail = (error) => ({ data: null, error });

const NOT_CONNECTED = 'This portal cannot reach the database.';
const NOT_APPLIED =
  'The Agent profile requests are not on this database yet — script 345 has not been run.';
const NOT_HELD = 'Your account does not hold People asking for an Agent profile.';

function readError(error, fallback) {
  if (error.code === 'PGRST202') return fail(NOT_APPLIED);
  if (error.code === '42501') return fail(NOT_HELD);
  return fail(error.message || fallback);
}

/** Everybody who has asked. Unseen first, then oldest. */
export async function fetchAgentProfileInterest() {
  if (!isSupabaseConfigured()) return fail(NOT_CONNECTED);
  const { data, error } = await supabase.rpc('fn_agent_profile_interest');
  if (error) return readError(error, 'The list could not be loaded.');
  return { data: data || [], error: null };
}

/** The figures, which are a different question from the list and are asked
 *  separately because this one can be answered without naming anybody. */
export async function fetchAgentProfileDemand() {
  if (!isSupabaseConfigured()) return fail(NOT_CONNECTED);
  const { data, error } = await supabase.rpc('fn_agent_profile_demand');
  if (error) return readError(error, 'The figures could not be loaded.');
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return { data: null, error: null };
  return {
    data: {
      people: row.people ?? 0,
      unseen: row.unseen ?? 0,
      answered: row.answered ?? 0,
      unitsManagedTotal: row.units_managed_total ?? 0,
      biggest: row.biggest ?? 0,
    },
    error: null,
  };
}

/**
 * Mark one request read.
 *
 * THE REASON IS KEPT, not collected and discarded. `view_agent_profile_interest`
 * declares requires_reason_capture -- the list NAMES PEOPLE, which an aggregate
 * count would not -- and the platform's log trigger refuses the action without
 * one. Eight characters is that trigger's own floor, checked here so somebody
 * is told by the form rather than by a trigger.
 */
export async function markAgentProfileRequestSeen(requestId, reason) {
  if (!isSupabaseConfigured()) return fail(NOT_CONNECTED);
  if (!requestId) return fail('Choose a request from the list.');
  if ((reason || '').trim().length < 8) {
    return fail('Say why you are opening this, in a sentence. It goes on the record with your name.');
  }

  const { error } = await supabase.rpc('fn_mark_agent_profile_request_seen', {
    p_request_id: requestId,
    p_reason: reason.trim(),
  });
  if (error) return readError(error, 'That could not be recorded.');
  return { data: true, error: null };
}
